import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import { Controller, useForm } from "react-hook-form";
import IntlCurrencyInput from "react-intl-currency-input";
import { useClients } from "../../../hooks/useClients";
import { useLawsuits } from "../../../hooks/useLawsuits";

const PAYMENT_METHODS = [
  "Pix",
  "Boleto",
  "Transferência",
  "Cartão",
  "Dinheiro",
  "Outros",
];

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

function toInputDate(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function todayInputDate() {
  return toInputDate(new Date());
}

function buildPayload(values) {
  const amount = Number(values.value) || 0;
  const amountReceived = Number(values.amount_received) || 0;
  const lawsuitId =
    values.lawsuit_id && String(values.lawsuit_id) !== "0"
      ? values.lawsuit_id
      : null;

  const payload = {
    client_id: values.client_id,
    lawsuit_id: lawsuitId,
    description: values.description?.trim() || null,
    due_date: values.due_date,
    value: amount,
    total_value: amount,
    fee: 0,
    late_fine: 0,
    extra_value: 0,
    payment_method: values.payment_method || "Outros",
    amount_received: amountReceived,
  };

  if (amount > 0 && amountReceived >= amount) {
    payload.confirmed_date = values.confirmed_date || todayInputDate();
  } else if (values.confirmed_date) {
    payload.confirmed_date = values.confirmed_date;
  }

  return payload;
}

export default function FormCobranca({
  show,
  onHide,
  payment,
  onSubmit,
  isSubmitting,
}) {
  const isEdit = Boolean(payment?.id);
  const { clients, isLoading: isLoadingClients } = useClients(undefined);
  const { lawsuits, isFetching: isLoadingLawsuits } = useLawsuits(undefined);
  const [valueAmount, setValueAmount] = useState(0);
  const [receivedAmount, setReceivedAmount] = useState(0);

  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      client_id: "",
      lawsuit_id: "",
      description: "",
      due_date: "",
      value: 0,
      payment_method: "Pix",
      amount_received: 0,
      confirmed_date: "",
    },
  });

  useEffect(() => {
    if (!show) return;

    if (payment) {
      const amount = Number(payment.value) || 0;
      const received = Number(payment.amount_received) || 0;
      setValueAmount(amount);
      setReceivedAmount(received);
      reset({
        client_id: payment.client_id ? String(payment.client_id) : "",
        lawsuit_id: payment.lawsuit_id ? String(payment.lawsuit_id) : "",
        description: payment.description || "",
        due_date: toInputDate(payment.due_date),
        value: amount,
        payment_method: payment.payment_method || "Pix",
        amount_received: received,
        confirmed_date: payment.confirmed_date
          ? toInputDate(payment.confirmed_date)
          : "",
      });
      return;
    }

    setValueAmount(0);
    setReceivedAmount(0);
    reset({
      client_id: "",
      lawsuit_id: "",
      description: "",
      due_date: "",
      value: 0,
      payment_method: "Pix",
      amount_received: 0,
      confirmed_date: "",
    });
  }, [show, payment, reset]);

  const submit = async (values) => {
    const payload = buildPayload(values);
    await onSubmit(payload);
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="lg">
      <Modal.Header closeButton className="modalGradient">
        <Modal.Title className="text-white">
          {isEdit ? "Editar cobrança" : "Nova Cobrança"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(submit)}>
          <Form.Group className="mb-3">
            <FloatingLabel label="Cliente">
              <Form.Select
                {...register("client_id", { required: true })}
                disabled={isLoadingClients}
              >
                <option value="">Selecione o cliente</option>
                {clients?.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Processo (opcional)">
              <Form.Select
                {...register("lawsuit_id")}
                disabled={isLoadingLawsuits}
              >
                <option value="">Sem processo</option>
                {lawsuits?.map((lawsuit) => (
                  <option key={lawsuit.id} value={lawsuit.id}>
                    {lawsuit.number}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <FloatingLabel label="Descrição">
              <Form.Control
                type="text"
                placeholder="Descrição"
                {...register("description", { required: true })}
              />
            </FloatingLabel>
          </Form.Group>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <FloatingLabel label="Valor">
                <Controller
                  control={control}
                  name="value"
                  rules={{ required: true, min: 0.01 }}
                  render={({ field }) => (
                    <IntlCurrencyInput
                      currency="BRL"
                      config={currencyConfig}
                      className="form-control"
                      value={valueAmount}
                      onChange={(event, value) => {
                        setValueAmount(value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </FloatingLabel>
            </div>
            <div className="col-md-6">
              <FloatingLabel label="Vencimento">
                <Form.Control
                  type="date"
                  {...register("due_date", { required: true })}
                />
              </FloatingLabel>
            </div>
          </div>

          <Form.Group className="mb-3">
            <FloatingLabel label="Forma de pagamento">
              <Form.Select {...register("payment_method", { required: true })}>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>

          {isEdit && (
            <Form.Group className="mb-3">
              <FloatingLabel label="Valor recebido">
                <Controller
                  control={control}
                  name="amount_received"
                  render={({ field }) => (
                    <IntlCurrencyInput
                      currency="BRL"
                      config={currencyConfig}
                      className="form-control"
                      value={receivedAmount}
                      onChange={(event, value) => {
                        setReceivedAmount(value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </FloatingLabel>
              <Form.Text className="text-muted">
                Informe o valor já recebido para status Parcial ou Recebido.
              </Form.Text>
            </Form.Group>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" type="button" onClick={onHide}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="btn-bg-primary text-dark borderColor"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Salvando...
                </>
              ) : isEdit ? (
                "Salvar"
              ) : (
                "Cadastrar"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
